/**
 * Serviço para gerenciar o modo Totem
 * 
 * Quando o acesso é feito via código de totem (TelevisaoPage), 
 * o app deve entrar em modo totem:
 * - Tela cheia automaticamente
 * - Sem cabeçalho (TopMenu)
 * - Sem controles de navegação
 * - Sem possibilidade de mudar abas ou trocar telas manualmente
 * 
 * Este modo é diferente do acesso normal de usuários logados.
 */

const TOTEM_MODE_KEY = 'totem_mode';
const TOTEM_MODE_TIMESTAMP_KEY = 'totem_mode_timestamp';

class TotemModeService {
    /**
     * Ativa o modo totem
     * Chamado quando o vínculo por código de totem é bem-sucedido
     */
    enableTotemMode(): void {
        // Persistir em sessionStorage e localStorage:
        // - sessionStorage cobre a sessão atual
        // - localStorage mantém o estado após fechar/reabrir navegador/dispositivo
        const timestamp = Date.now().toString();
        sessionStorage.setItem(TOTEM_MODE_KEY, 'true');
        sessionStorage.setItem(TOTEM_MODE_TIMESTAMP_KEY, timestamp);
        localStorage.setItem(TOTEM_MODE_KEY, 'true');
        localStorage.setItem(TOTEM_MODE_TIMESTAMP_KEY, timestamp);
        console.log('🖥️ Modo Totem ativado');
    }

    /**
     * Desativa o modo totem
     * Chamado quando o usuário sai do modo totem ou faz logout
     */
    disableTotemMode(): void {
        sessionStorage.removeItem(TOTEM_MODE_KEY);
        sessionStorage.removeItem(TOTEM_MODE_TIMESTAMP_KEY);
        localStorage.removeItem(TOTEM_MODE_KEY);
        localStorage.removeItem(TOTEM_MODE_TIMESTAMP_KEY);
        console.log('🖥️ Modo Totem desativado');
    }

    /**
     * Verifica se o app está em modo totem
     * Verifica sessionStorage e também parâmetro de URL ?mode=totem ou ?totem=true para facilitar testes e robustez
     */
    isTotemMode(): boolean {
        // Verificar sessionStorage (fluxo principal via QR Code)
        if (sessionStorage.getItem(TOTEM_MODE_KEY) === 'true') {
            return true;
        }

        // Verificar localStorage (persistência após reiniciar aba/navegador/dispositivo)
        if (localStorage.getItem(TOTEM_MODE_KEY) === 'true') {
            return true;
        }

        // Verificar URL (fallback para testes ou links diretos)
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            return params.get('mode') === 'totem' || params.get('totem') === 'true';
        }

        return false;
    }

    /**
     * Obtém o timestamp de quando o modo totem foi ativado
     */
    getTotemModeTimestamp(): number | null {
        const timestamp = sessionStorage.getItem(TOTEM_MODE_TIMESTAMP_KEY) ||
            localStorage.getItem(TOTEM_MODE_TIMESTAMP_KEY);
        return timestamp ? parseInt(timestamp, 10) : null;
    }

    /**
     * Tenta entrar em tela cheia
     * @returns Promise<boolean> - true se conseguiu, false se não
     */
    async requestFullscreen(): Promise<boolean> {
        try {
            const docElement = document.documentElement;

            if (docElement.requestFullscreen) {
                await docElement.requestFullscreen();
                return true;
            } else if ((docElement as any).webkitRequestFullscreen) {
                await (docElement as any).webkitRequestFullscreen();
                return true;
            } else if ((docElement as any).msRequestFullscreen) {
                await (docElement as any).msRequestFullscreen();
                return true;
            } else if ((docElement as any).mozRequestFullScreen) {
                await (docElement as any).mozRequestFullScreen();
                return true;
            }

            console.warn('⚠️ Navegador não suporta tela cheia');
            return false;
        } catch (error) {
            console.warn('⚠️ Erro ao entrar em tela cheia:', error);
            return false;
        }
    }

    /**
     * Sai da tela cheia
     */
    async exitFullscreen(): Promise<boolean> {
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
                return true;
            } else if ((document as any).webkitExitFullscreen) {
                await (document as any).webkitExitFullscreen();
                return true;
            } else if ((document as any).msExitFullscreen) {
                await (document as any).msExitFullscreen();
                return true;
            } else if ((document as any).mozCancelFullScreen) {
                await (document as any).mozCancelFullScreen();
                return true;
            }
            return false;
        } catch (error) {
            console.warn('⚠️ Erro ao sair da tela cheia:', error);
            return false;
        }
    }

    /**
     * Verifica se está em tela cheia
     */
    isFullscreen(): boolean {
        return !!(
            document.fullscreenElement ||
            (document as any).webkitFullscreenElement ||
            (document as any).msFullscreenElement ||
            (document as any).mozFullScreenElement
        );
    }

    /**
     * Bloqueia teclas de navegação quando em modo totem
     * @param event - Evento de teclado
     * @returns boolean - true se deve bloquear o evento
     */
    shouldBlockKeyboardEvent(event: KeyboardEvent): boolean {
        if (!this.isTotemMode()) {
            return false;
        }

        // Lista de teclas que devem ser bloqueadas em modo totem
        const blockedKeys = [
            'Escape', // Não deixar sair da tela cheia
            'F11',    // Não deixar alternar tela cheia manualmente
            'Tab',    // Não deixar navegar entre elementos
            'Alt',    // Bloquear combinações Alt+
        ];

        // Bloquear F5 (refresh) - mas permitir Ctrl+Shift+R para casos extremos
        if (event.key === 'F5' && !event.ctrlKey) {
            return true;
        }

        // Bloquear Alt+Tab, Alt+F4, etc
        if (event.altKey) {
            return true;
        }

        // Bloquear teclas específicas
        if (blockedKeys.includes(event.key)) {
            return true;
        }

        return false;
    }
}

export const totemModeService = new TotemModeService();
